import type { Row, RowSelectionState } from "@tanstack/react-table";
import { type MouseEvent, useCallback, useRef } from "react";

import {
	EMPTY_ROW_SELECTION,
	useScopedRowSelectionState,
} from "@/shared/hooks/useScopedRowSelectionState";
import type { Entry } from "@/shared/types/entries";

export interface EntriesTableSelectionController {
	selectionMode: boolean;
	selectedCount: number;
	selectedEntries: Entry[];
	selectedIds: number[];
	allCurrentPageSelected: boolean;
	someCurrentPageSelected: boolean;
	enterSelectionMode: (entry: Entry) => void;
	exitSelectionMode: () => void;
	selectAll: () => void;
	handleIconClick: (entry: Entry, event: MouseEvent) => void;
	handleRowSelect: (entry: Entry, event: MouseEvent) => void;
}

export { EMPTY_ROW_SELECTION } from "@/shared/hooks/useScopedRowSelectionState";
export const useEntriesTableSelectionState = useScopedRowSelectionState;

interface UseEntriesTableSelectionControllerOptions {
	displayedRows: Row<Entry>[];
	rowSelection: RowSelectionState;
	selectionMode: boolean;
	setSelectedRowIds: (rows: RowSelectionState, active?: boolean) => void;
}

export function useEntriesTableSelectionController({
	displayedRows,
	rowSelection,
	selectionMode,
	setSelectedRowIds,
}: UseEntriesTableSelectionControllerOptions): EntriesTableSelectionController {
	const lastClickedId = useRef<string | null>(null);

	const selectedRows = displayedRows.filter((row) => row.getIsSelected());
	const selectedEntries = selectedRows.map((row) => row.original);
	const selectedIds = selectedEntries.map((entry) => entry.id);
	const selectedCount = selectedRows.length;
	const allCurrentPageSelected =
		displayedRows.length > 0 &&
		displayedRows.every((row) => row.getIsSelected());
	const someCurrentPageSelected =
		displayedRows.some((row) => row.getIsSelected()) && !allCurrentPageSelected;

	const enterSelectionMode = useCallback(
		(entry: Entry) => {
			const id = String(entry.id);
			setSelectedRowIds({ [id]: true });
			lastClickedId.current = id;
		},
		[setSelectedRowIds],
	);

	const exitSelectionMode = useCallback(() => {
		setSelectedRowIds(EMPTY_ROW_SELECTION, false);
		lastClickedId.current = null;
	}, [setSelectedRowIds]);

	const handleSelect = useCallback(
		(entry: Entry, event: MouseEvent) => {
			const targetId = String(entry.id);
			const hasCtrl = event.ctrlKey || event.metaKey;
			const hasShift = event.shiftKey;
			const orderedIds = displayedRows.map((row) => row.id);

			if (hasShift && lastClickedId.current !== null) {
				const from = orderedIds.indexOf(lastClickedId.current);
				const to = orderedIds.indexOf(targetId);

				if (from !== -1 && to !== -1) {
					const start = Math.min(from, to);
					const end = Math.max(from, to);
					const rangeIds = orderedIds.slice(start, end + 1);
					const next: RowSelectionState = hasCtrl ? { ...rowSelection } : {};
					for (const id of rangeIds) next[id] = true;
					setSelectedRowIds(next);
					return;
				}
			}

			if (hasCtrl) {
				const next = { ...rowSelection };
				if (next[targetId]) delete next[targetId];
				else next[targetId] = true;
				setSelectedRowIds(next);
			} else {
				setSelectedRowIds({ [targetId]: true });
			}

			lastClickedId.current = targetId;
		},
		[displayedRows, rowSelection, setSelectedRowIds],
	);

	const handleIconClick = useCallback(
		(entry: Entry, event: MouseEvent) => {
			if (selectionMode) {
				handleSelect(entry, event);
				return;
			}

			enterSelectionMode(entry);
		},
		[selectionMode, handleSelect, enterSelectionMode],
	);

	const handleRowSelect = useCallback(
		(entry: Entry, event: MouseEvent) => {
			handleSelect(entry, event);
		},
		[handleSelect],
	);

	const selectAll = useCallback(() => {
		const next = { ...rowSelection };
		if (allCurrentPageSelected) {
			for (const row of displayedRows) delete next[row.id];
		} else {
			for (const row of displayedRows) next[row.id] = true;
		}
		setSelectedRowIds(next);
	}, [allCurrentPageSelected, displayedRows, rowSelection, setSelectedRowIds]);

	return {
		selectionMode,
		selectedCount,
		selectedEntries,
		selectedIds,
		allCurrentPageSelected,
		someCurrentPageSelected,
		enterSelectionMode,
		exitSelectionMode,
		selectAll,
		handleIconClick,
		handleRowSelect,
	};
}
